import * as React from "react";
import get from "lodash/get";

import "./Dropdown.css";
import * as actions from "../../actions";
import { isEditMode } from "../../utils/sendClickToMerchant";
import { ComponentBase, ComponentBaseProps } from "../ComponentBase";
import { flattenObject } from "../../utils/flattenObject";
import { generateErrorId } from "../../utils/string";

// Values leave as one delimited string.
export const MULTI_VALUE_DELIMITER = ", ";

// Mirrors max-height in Dropdown.css; shouldOpenUpward needs the number.
export const PANEL_MAX_HEIGHT = 220;

// Mirrors .Dropdown-option's box in Dropdown.css (6px padding twice plus the
// line) plus the panel's own 2px padding. Only used to estimate how tall the
// panel will actually be, so a three-option list near the bottom edge doesn't
// flip up as though it needed all 220px.
export const PANEL_ROW_HEIGHT = 30;

export const estimatePanelHeight = (optionCount: number): number =>
  Math.min(PANEL_MAX_HEIGHT, optionCount * PANEL_ROW_HEIGHT + 4);

interface ErrorAttributes {
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

export interface DropdownOption {
  label: string;
  value: string;
}

export interface DropdownProps extends ComponentBaseProps {
  clearWidgetFormError: typeof actions.clearWidgetFormError;
  formErrors: {
    [key: string]: { [index: string]: any };
  };
  configName: string;
  options?: DropdownOption[];
  // The empty <option> on a single select; the trigger's text on a multi.
  placeholder?: string;
  fieldLabel?: string;
  multiple?: boolean;
  style?: React.CSSProperties;
}

/**
 * Whether the panel opens upward. Only a native select's popup escapes the
 * widget iframe, so this one has to stay inside it. Pure, so it is testable
 * without a DOM.
 */
export const shouldOpenUpward = (
  triggerBottom: number,
  triggerTop: number,
  viewportHeight: number,
  panelHeight: number
): boolean => {
  const roomBelow = viewportHeight - triggerBottom;

  if (roomBelow >= panelHeight) {
    return false;
  }

  // Only flip if going up is actually better; otherwise stay put and scroll.
  return triggerTop > roomBelow;
};

/**
 * A merchant-authored choice field.
 *
 * Single is a native `<select>` — browser-painted popup, OS picker on mobile.
 * Multi is a collapsed trigger opening a panel: a `<select multiple>` needs
 * ctrl/cmd-click for a second option and wipes the selection on a plain click.
 *
 * The selection lives only in form `data`; component state holds just the
 * panel's open/flip/active-row, which a screen change is free to lose.
 */
export class Dropdown extends ComponentBase<DropdownProps> {
  componentWillUnmount() {
    super.componentWillUnmount();
    this.stopWatchingOutsideClicks();
  }

  // A blank value would collide with the placeholder's slot.
  // Deduped by value, first occurrence wins. A duplicate is the same choice, so
  // two rows for it would both light up when either is picked, and the second
  // <option> in a native select is unreachable anyway. Dropping them here keeps
  // toggleOption and the controlled select from ever seeing one. `indexOf`
  // rather than a Set: this file ships to browsers the SDK still polyfills for.
  private validOptions = (): DropdownOption[] => {
    const seen: string[] = [];

    return (this.props.options || []).filter((option) => {
      if (!option || !option.value || seen.indexOf(option.value) !== -1) {
        return false;
      }

      seen.push(option.value);

      return true;
    });
  };

  // Splitting on "," round trips because the builder strips commas from values.
  // Coerced: a `defaultValue` from an old config can arrive as a number.
  private selectedValues = (): string[] => {
    const raw = get(this.props.data, this.props.name);

    return raw === undefined || raw === null || raw === ""
      ? []
      : String(raw)
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean);
  };

  private emit = (values: string[]) => {
    const value = values.join(MULTI_VALUE_DELIMITER);

    this.onFormChange(value);

    // Clear an existing error on selection rather than on the next submit.
    if (flattenObject(get(this.props, "formErrors", {}))[this.props.name]) {
      this.validateField(true, value);
    }
  };

  handleOnChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    this.emit(event.target.value === "" ? [] : [event.target.value]);
  };

  toggleOption = (value: string) => {
    const selected = this.selectedValues();

    this.emit(
      selected.indexOf(value) === -1
        ? [...selected, value]
        : selected.filter((entry) => entry !== value)
    );
  };

  // Keeps the keyboard position and the active highlight on the row the mouse
  // just used, so arrowing on after clicking continues from there.
  onOptionClick = (index: number, value: string) => {
    this.setState({ dropdownActiveIndex: index });
    this.toggleOption(value);
  };

  private startWatchingOutsideClicks = () => {
    document.addEventListener("mousedown", this.onDocumentMouseDown);
    // The widget is its own document in an iframe, so clicking the merchant's
    // page fires no mousedown here and blurs the trigger with a null
    // relatedTarget. Losing the frame is the only signal for that case. Row
    // clicks keep focus in the document, so this does not fire for them.
    window.addEventListener("blur", this.closePanel);
  };

  private stopWatchingOutsideClicks = () => {
    document.removeEventListener("mousedown", this.onDocumentMouseDown);
    window.removeEventListener("blur", this.closePanel);
  };

  private onDocumentMouseDown = (event: MouseEvent) => {
    if (
      this.component &&
      !this.component.contains(event.target as Node) &&
      this.state.isDropdownOpen
    ) {
      this.closePanel();
    }
  };

  openPanel = () => {
    const rect = this.component && this.component.getBoundingClientRect();
    const options = this.validOptions();

    this.setState({
      isDropdownOpen: true,
      dropdownOpensUp: rect
        ? shouldOpenUpward(
            rect.bottom,
            rect.top,
            window.innerHeight,
            estimatePanelHeight(options.length)
          )
        : false,
      // Start on the first selected row, so arrows continue from their answer.
      // Undefined with no options: aria-activedescendant would otherwise point
      // at a row that does not exist.
      dropdownActiveIndex: options.length
        ? Math.max(
            options.findIndex(
              (option) => this.selectedValues().indexOf(option.value) !== -1
            ),
            0
          )
        : undefined,
    });

    this.startWatchingOutsideClicks();
  };

  closePanel = () => {
    this.setState({ isDropdownOpen: false, dropdownActiveIndex: undefined });
    this.stopWatchingOutsideClicks();
  };

  onTriggerClick = () => {
    // The builder's invisible drag layer would cover the panel and block
    // resizing, so the control stays collapsed on the edit canvas.
    if (isEditMode()) {
      return;
    }

    if (this.state.isDropdownOpen) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  };

  // Tabbing away leaves the panel floating over the widget otherwise. Only acts
  // on a real destination: clicking a row gives a null relatedTarget, and
  // closing there would make the panel impossible to pick from. Outside clicks
  // that land on nothing focusable are handled by the document listener.
  onTriggerBlur = (event: React.FocusEvent<HTMLElement>) => {
    const next = event.relatedTarget as Node | null;

    if (next && this.component && !this.component.contains(next)) {
      this.closePanel();
    }
  };

  onTriggerKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    const { isDropdownOpen, dropdownActiveIndex } = this.state;
    const options = this.validOptions();

    if (event.key === "Escape") {
      if (isDropdownOpen) {
        event.preventDefault();
        this.closePanel();
      }

      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();

      if (!isDropdownOpen) {
        this.openPanel();

        return;
      }

      if (options.length === 0) {
        return;
      }

      const step = event.key === "ArrowDown" ? 1 : -1;
      const current =
        dropdownActiveIndex === undefined ? 0 : dropdownActiveIndex;
      // Clamped, not wrapped: wrapping a scrolling panel reads as it jumping.
      const next = Math.min(Math.max(current + step, 0), options.length - 1);

      this.setState({ dropdownActiveIndex: next }, () =>
        this.scrollActiveOptionIntoView(next)
      );

      return;
    }

    if (
      isDropdownOpen &&
      dropdownActiveIndex !== undefined &&
      options[dropdownActiveIndex] &&
      (event.key === "Enter" || event.key === " " || event.key === "Spacebar")
    ) {
      // Stays open — picking several is the point.
      event.preventDefault();
      this.toggleOption(options[dropdownActiveIndex].value);
    }
  };

  // With a fieldLabel the <label htmlFor> names the control natively; without
  // one the fallback has to sit on the focusable element, since a name on the
  // role=group wrapper names neither the select nor the trigger.
  // `data` stores values; the trigger has to read as the labels the visitor
  // picked. A value with no matching option falls back to itself, so an option
  // deleted after someone answered still shows something.
  private selectedLabels = (
    options: DropdownOption[],
    selected: string[]
  ): string =>
    selected
      .map((value) => {
        const option = options.filter((entry) => entry.value === value)[0];

        return option ? option.label || option.value : value;
      })
      .join(MULTI_VALUE_DELIMITER);

  private controlName = () =>
    this.props.fieldLabel
      ? {}
      : { "aria-label": get(this.props, "label", "") };

  // The panel scrolls at 220px, and the keyboard moves a highlight rather than
  // focus, so nothing brings the active row into view on its own.
  private scrollActiveOptionIntoView = (index: number) => {
    const row = document.getElementById(
      `${this.props.configName}-option-${index}`
    );

    if (row && row.scrollIntoView) {
      // "nearest" scrolls only when the row is actually out of view; the
      // boolean form is the fallback for browsers without the options object.
      try {
        row.scrollIntoView({ block: "nearest" });
      } catch (_error) {
        row.scrollIntoView(false);
      }
    }
  };

  private renderPanel = (options: DropdownOption[], selected: string[]) => {
    const { configName } = this.props;
    const { dropdownOpensUp, dropdownActiveIndex } = this.state;

    return (
      <div
        className={`Dropdown-panel ${
          dropdownOpensUp ? "Dropdown-panel-up" : ""
        }`}
        role="listbox"
        aria-multiselectable={true}
        id={`${configName}-panel`}
      >
        {options.map((option, index) => {
          const isSelected = selected.indexOf(option.value) !== -1;

          return (
            <div
              // Keyed by index, not value: the builder only warns about
              // duplicate values, so two identical values would collide here
              // and the ids below are index-based anyway.
              key={index}
              id={`${configName}-option-${index}`}
              className={`Dropdown-option ${
                isSelected ? "Dropdown-option-selected" : ""
              } ${index === dropdownActiveIndex ? "Dropdown-option-active" : ""}`}
              role="option"
              aria-selected={isSelected}
              // Focus stays on the trigger — that is what aria-activedescendant
              // requires, and it stops a row click blurring the control.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => this.onOptionClick(index, option.value)}
            >
              <span className="Dropdown-option-label">
                {option.label || option.value}
              </span>
              <svg
                viewBox="0 0 512 512"
                className="Dropdown-option-check"
                aria-hidden={true}
              >
                <path
                  fill="currentColor"
                  d="M173.898 439.404l-166.4-166.4c-9.997-9.997-9.997-26.206 0-36.204l36.203-36.204c9.997-9.998 26.207-9.998 36.204 0L192 312.69 432.095 72.596c9.997-9.997 26.207-9.997 36.204 0l36.203 36.204c9.997 9.997 9.997 26.206 0 36.204l-294.4 294.401c-9.998 9.997-26.207 9.997-36.204-.001z"
                />
              </svg>
            </div>
          );
        })}
      </div>
    );
  };

  private renderMultiSelect = (
    options: DropdownOption[],
    selected: string[],
    className: string,
    describedBy: ErrorAttributes
  ) => {
    const { configName, placeholder } = this.props;
    const { isDropdownOpen, dropdownActiveIndex } = this.state;
    const hasSelection = selected.length > 0;

    return (
      <React.Fragment>
        <button
          type="button"
          id={`${configName}-select`}
          className={`Dropdown-trigger ${
            hasSelection ? "" : "Dropdown-select-placeholder"
          } ${className}`}
          role="combobox"
          aria-expanded={Boolean(isDropdownOpen)}
          aria-haspopup="listbox"
          // Only while the node exists — a dangling aria-controls reference is
          // invalid.
          {...(isDropdownOpen
            ? { "aria-controls": `${configName}-panel` }
            : {})}
          {...this.controlName()}
          {...(isDropdownOpen && dropdownActiveIndex !== undefined
            ? {
                "aria-activedescendant": `${configName}-option-${dropdownActiveIndex}`,
              }
            : {})}
          {...describedBy}
          onClick={this.onTriggerClick}
          onKeyDown={this.onTriggerKeyDown}
          onBlur={this.onTriggerBlur}
        >
          <span className="Dropdown-trigger-text">
            {hasSelection
              ? this.selectedLabels(options, selected)
              : placeholder || ""}
          </span>
          <span className="Dropdown-trigger-arrow" aria-hidden={true} />
        </button>
        {isDropdownOpen && this.renderPanel(options, selected)}
      </React.Fragment>
    );
  };

  private renderSelect = (
    options: DropdownOption[],
    selected: string[],
    className: string,
    describedBy: ErrorAttributes
  ) => (
    <select
      id={`${this.props.configName}-select`}
      // No ::placeholder exists on a <select>, so InputPlaceholderStyles
      // targets this class instead.
      className={`Dropdown-select ${
        selected.length === 0 ? "Dropdown-select-placeholder" : ""
      } ${className}`}
      value={selected[0] || ""}
      onChange={this.handleOnChange}
      {...this.controlName()}
      {...describedBy}
      tabIndex={0}
    >
      <option value="">{this.props.placeholder}</option>
      {options.map((option, index) => (
        <option key={index} value={option.value}>
          {option.label || option.value}
        </option>
      ))}
    </select>
  );

  render() {
    const { configName, fieldLabel, multiple, name, style } = this.props;

    const options = this.validOptions();
    const selected = this.selectedValues();
    const isPreviewMode = !isEditMode();
    const formErrors = flattenObject(get(this.props, "formErrors", {}));
    const hasError = formErrors[name];

    const labelId = `${configName}-label`;
    const selectId = `${configName}-select`;

    // ARIA goes on the control; only the focused element gets announced.
    const describedBy: ErrorAttributes = hasError
      ? { "aria-describedby": generateErrorId(name), "aria-invalid": true }
      : {};

    // `style` lands on the wrapper (the element the builder positions and
    // resizes), so the wrapper carries the field chrome — which is what makes
    // the border editable down to nothing — and `input-error` with it.
    const wrapperClassName = `Dropdown ${hasError ? "input-error" : ""} ${
      this.state.isDropdownOpen ? "Dropdown-open" : ""
    }`;
    const controlClassName = isPreviewMode ? "Dropdown-control-preview" : "";

    // A fieldLabel stacks above the control inside this box, so a configured
    // height crushes both and the control spills past the border (overflow is
    // deliberately visible so the panel can escape). Honour the height as a
    // floor and let the box grow. BirthdayInput sidesteps this by seeding ~82px
    // in its palette template instead.
    const wrapperStyle =
      fieldLabel && style && style.height
        ? { ...style, height: undefined, minHeight: style.height }
        : style;

    return (
      <div
        id={configName}
        className={wrapperClassName}
        style={wrapperStyle}
        // group rather than fieldset: a fieldset changes the box model this
        // flex layout depends on.
        role="group"
        {...(fieldLabel ? { "aria-labelledby": labelId } : {})}
        onDragStart={this.onDragStart}
        ref={(component) => (this.component = component)}
      >
        {fieldLabel && (
          <label
            id={labelId}
            // Both controls are labelable — a <select> and a <button>.
            htmlFor={selectId}
            className="Dropdown-label"
          >
            {fieldLabel}
          </label>
        )}
        {multiple
          ? this.renderMultiSelect(
              options,
              selected,
              controlClassName,
              describedBy
            )
          : this.renderSelect(options, selected, controlClassName, describedBy)}
      </div>
    );
  }
}
